import { useEffect, useMemo, useState } from 'react';
import {
  MessageCircle,
  Send,
  Clock,
  CheckCircle,
  Play,
  X,
  ArrowRight,
  User,
  Phone,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Progress } from '@/components/ui/progress';
import { getLandingData, trackEvent, submitQuiz } from '@/lib/api';

// Convert 2-letter country code (or emoji) to a flag emoji. Works on all
// platforms once the "Twemoji Country Flags" font-face rule is loaded.
const toFlagEmoji = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim();
  if (/[\uD83C][\uDDE6-\uDDFF]/.test(s)) return s;
  const clean = s.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
  if (clean.length !== 2) return s;
  return String.fromCodePoint(
    ...clean.split('').map((c) => c.charCodeAt(0) + 127397)
  );
};

// ---------------------------------------------------------------------------
// Small browser helpers
// ---------------------------------------------------------------------------

const readCookie = (name) => {
  const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return m ? m[2] : '';
};

const readUtm = () => {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get('utm_source') || '',
    utm_medium: p.get('utm_medium') || '',
    utm_campaign: p.get('utm_campaign') || '',
  };
};

// Build an <img src> value for a manager/gallery photo that may be stored
// either as a data-URI, a full http(s) URL, or just a filename.
const buildAssetSrc = (value) => {
  if (!value) return null;
  if (value.startsWith('data:') || value.startsWith('http')) return value;
  return `${process.env.REACT_APP_BACKEND_URL}/api/uploads/base64/${value}`;
};

// ---------------------------------------------------------------------------
// Pixel loaders (injected once per pageload)
// ---------------------------------------------------------------------------

const injectMetaPixels = (pixelIds) => {
  if (!pixelIds?.length || window.fbq) return;
  const tag = document.createElement('script');
  tag.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    ${pixelIds.map((id) => `fbq('init', '${id}');`).join('\n    ')}
    fbq('track', 'PageView');
  `;
  document.head.appendChild(tag);
};

const injectTikTokPixels = (pixelIds) => {
  if (!pixelIds?.length || window.ttq) return;
  const tag = document.createElement('script');
  tag.innerHTML = `
    !function (w, d, t) {
      w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
      ${pixelIds.map((id) => `ttq.load('${id}');`).join('\n      ')}
      ttq.page();
    }(window, document, 'ttq');
  `;
  document.head.appendChild(tag);
};

// ---------------------------------------------------------------------------
// Sub-sections (kept in the same file on purpose — small & page-local)
// ---------------------------------------------------------------------------

const QuizIntro = ({ config, questionCount, onStart }) => (
  <div className="min-h-screen relative overflow-hidden flex items-center justify-center p-4 bg-slate-950">
    {/* Deep green layered background with soft glow */}
    <div className="absolute inset-0 bg-gradient-to-br from-emerald-950 via-teal-900 to-green-950" />
    <div className="absolute -top-40 -left-40 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />
    <div className="absolute -bottom-40 -right-40 w-[32rem] h-[32rem] bg-green-500/20 rounded-full blur-3xl" />

    <div className="relative max-w-2xl w-full text-center">
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-400/20 text-emerald-300 px-4 py-2 rounded-full text-sm mb-6 backdrop-blur">
          <CheckCircle className="w-4 h-4" />
          Проверка за 60 секунд
        </div>
        <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight tracking-tight">
          {config.hero_title || 'Официальная работа в Великобритании и Европе'}
        </h1>
        <p className="text-lg md:text-xl text-slate-300 mb-8">
          {config.hero_subtitle || 'Пройдите короткий тест и узнайте, подходите ли вы'}
        </p>
      </div>

      <Button
        size="lg"
        className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-white px-10 py-6 text-lg rounded-full shadow-xl shadow-emerald-500/30 transition-all hover:scale-105 hover:shadow-2xl hover:shadow-green-400/40"
        onClick={onStart}
        data-testid="start-quiz-button"
      >
        {config.hero_button || 'Подать заявку'}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>

      <p className="text-slate-500 text-sm mt-6">
        {questionCount} вопросов • Бесплатно • Анонимно
      </p>
    </div>
  </div>
);

const ContactForm = ({ onSubmit }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  const handleNameChange = (e) => {
    // Allow letters (any language) + spaces + hyphens/apostrophes. Strip digits and symbols.
    const cleaned = e.target.value.replace(/[0-9]/g, '').replace(/[^\p{L}\s'-]/gu, '');
    setName(cleaned);
  };

  const handlePhoneChange = (e) => {
    // Keep only leading + and digits. User must include country code.
    let v = e.target.value.replace(/[^0-9+]/g, '');
    // Only one leading +
    if (v.indexOf('+') > 0) v = v.replace(/\+/g, '');
    if (v.length > 1) v = v[0] + v.slice(1).replace(/\+/g, '');
    setPhone(v);
  };

  const phoneOk = /^\+\d{7,15}$/.test(phone.trim());
  const nameOk = name.trim().length >= 2;
  const canSubmit = nameOk && phoneOk;

  const submit = (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    onSubmit({ name: name.trim(), phone: phone.trim() });
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center p-4 bg-slate-950">
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-950 via-teal-900 to-green-950" />
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -right-40 w-[32rem] h-[32rem] bg-green-500/20 rounded-full blur-3xl" />

      <form
        onSubmit={submit}
        className="relative max-w-md w-full bg-white/5 border border-white/10 backdrop-blur rounded-2xl p-8 space-y-5"
        data-testid="contact-form"
      >
        <div className="text-center space-y-2 mb-2">
          <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
            Оставьте контакты для связи
          </h2>
          <p className="text-slate-300 text-sm">
            Менеджер свяжется с вами после короткой проверки
          </p>
        </div>

        <div className="space-y-2">
          <div className="relative">
            <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              type="text"
              value={name}
              onChange={handleNameChange}
              placeholder="Ваше имя"
              className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 pl-10 h-12 focus-visible:ring-emerald-400"
              data-testid="contact-name-input"
              autoFocus
              autoComplete="name"
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="relative">
            <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              type="tel"
              value={phone}
              onChange={handlePhoneChange}
              placeholder="+998 90 123 45 67"
              className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 pl-10 h-12 focus-visible:ring-emerald-400 font-mono"
              data-testid="contact-phone-input"
              inputMode="tel"
              autoComplete="tel"
            />
          </div>
          {phone && !phoneOk && (
            <p className="text-xs text-red-300 pl-1">
              Введите номер с кодом страны, например +998901234567
            </p>
          )}
        </div>

        <Button
          type="submit"
          size="lg"
          disabled={!canSubmit}
          className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-white h-12 rounded-full text-base font-medium shadow-lg shadow-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none transition-all"
          data-testid="contact-submit-button"
        >
          Ответить
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </form>
    </div>
  );
};

const QuizQuestionView = ({ question, stepIndex, total, progressPct, onPick }) => {
  const [textValue, setTextValue] = useState('');
  
  // Determine if this is a text input question:
  // 1. Explicit input_type of 'text' or 'number'
  // 2. No options available (empty array)
  const hasNoOptions = !question?.options || question.options.length === 0;
  const isText = question?.input_type === 'text' || question?.input_type === 'number' || hasNoOptions;
  const isNumber = question?.input_type === 'number';
  
  const minOk = isNumber
    ? Number(textValue) > 0 && Number(textValue) < 120
    : textValue.trim().length >= 1;

  const submitText = () => {
    if (!minOk) return;
    onPick({ id: 'text-answer', text: textValue.trim() });
    setTextValue('');
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col p-4 bg-slate-950">
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-950 via-teal-900 to-green-950" />
      <div className="absolute top-1/3 -left-40 w-96 h-96 bg-emerald-500/15 rounded-full blur-3xl" />
      <div className="absolute bottom-1/3 -right-40 w-96 h-96 bg-green-500/15 rounded-full blur-3xl" />

      <div className="relative max-w-2xl w-full mx-auto mb-8">
        <div className="flex items-center justify-between text-sm text-slate-400 mb-2">
          <span>Вопрос {stepIndex + 1} из {total}</span>
          <span>{Math.round(progressPct)}%</span>
        </div>
        <Progress value={progressPct} className="h-2 bg-slate-800" />
      </div>

      <div className="relative flex-1 flex items-center justify-center">
        <div className="max-w-2xl w-full">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-8 tracking-tight">
            {question?.question}
          </h2>

          {isText ? (
            <form
              onSubmit={(e) => { e.preventDefault(); submitText(); }}
              className="max-w-md mx-auto space-y-4"
              data-testid="quiz-text-form"
            >
              <Input
                type={isNumber ? 'number' : 'text'}
                inputMode={isNumber ? 'numeric' : 'text'}
                value={textValue}
                onChange={(e) => {
                  const v = isNumber ? e.target.value.replace(/[^0-9]/g, '') : e.target.value;
                  setTextValue(v);
                }}
                placeholder={question?.placeholder || (isNumber ? 'Введите число' : 'Ваш ответ')}
                className="bg-white/10 border-white/20 text-white text-center placeholder:text-slate-400 h-14 text-xl focus-visible:ring-emerald-400"
                data-testid="quiz-text-input"
                autoFocus
              />
              <Button
                type="submit"
                size="lg"
                disabled={!minOk}
                className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-white h-12 rounded-full disabled:opacity-40"
                data-testid="quiz-text-submit"
              >
                Далее
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          ) : (
            <div className="grid gap-3">
              {question?.options?.map((opt, i) => (
                <button
                  key={opt.id || i}
                  onClick={() => onPick(opt)}
                  className="w-full p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-emerald-400/60 rounded-xl text-left transition-all hover:scale-[1.02] backdrop-blur group"
                  data-testid={`quiz-option-${i}`}
                >
                  <div className="flex items-center gap-3">
                    {opt.emoji && <span className="text-2xl">{opt.emoji}</span>}
                    <span className="text-white text-lg group-hover:text-emerald-300 transition-colors">
                      {opt.text}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const VacancyShowcase = ({ workType, vacancies, onSelect }) => {
  const matching = vacancies.filter((v) => {
    if (workType === 'Любая стабильная') return true;
    if (v.work_type && v.work_type.toLowerCase() === workType.toLowerCase()) return true;
    const t = (v.title || '').toLowerCase();
    return t.includes(workType.toLowerCase());
  });

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col p-4 bg-slate-950" data-testid="vacancy-showcase">
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-950 via-teal-900 to-green-950" />
      <div className="absolute top-1/4 -left-40 w-96 h-96 bg-emerald-500/15 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 -right-40 w-96 h-96 bg-green-500/15 rounded-full blur-3xl" />

      <div className="relative max-w-3xl w-full mx-auto py-8">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-400/20 text-emerald-300 px-4 py-2 rounded-full text-sm mb-4 backdrop-blur">
            <CheckCircle className="w-4 h-4" />
            Актуальные вакансии для «{workType}»
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
            Какая вакансия вам интересна?
          </h2>
          <p className="text-slate-300 text-sm mt-2">Выберите одну, чтобы продолжить</p>
        </div>

        <div className="grid gap-3 mb-2 max-h-[60vh] overflow-y-auto pr-1">
          {matching.length === 0 ? (
            <div className="text-center py-8 space-y-4">
              <p className="text-slate-300">
                Пока нет активных вакансий по этой категории — менеджер подберёт индивидуально.
              </p>
              <Button
                size="lg"
                onClick={() => onSelect(null)}
                className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-white h-12 rounded-full px-8"
                data-testid="vacancy-showcase-continue"
              >
                Продолжить
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          ) : matching.map((v, i) => (
            <button
              key={v.id}
              onClick={() => onSelect(v)}
              className="text-left bg-white/5 border border-white/10 backdrop-blur rounded-xl p-4 hover:bg-white/10 hover:border-emerald-400/60 transition-colors"
              data-testid={`vacancy-option-${i}`}
            >
              <div className="flex items-start gap-3">
                <span className="text-3xl">{toFlagEmoji(v.flag || v.country)}</span>
                <div className="flex-1">
                  <h3 className="font-bold text-white">{v.title}</h3>
                  <p className="text-sm text-slate-400">{v.country}</p>
                  {v.salary_from && (
                    <div className="mt-2 text-emerald-300 font-semibold text-sm">{v.salary_from}</div>
                  )}
                  {v.description && (
                    <p className="mt-1 text-sm text-slate-300">{v.description}</p>
                  )}
                </div>
                <ArrowRight className="w-5 h-5 text-emerald-400 shrink-0 mt-1" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const ManagerReveal = ({ manager, settings, onContact }) => {
  // Which contact buttons should render — respects per-manager toggles,
  // global site toggles and presence of the link itself.
  const showTg =
    !!manager.telegram_link &&
    manager.show_telegram !== false &&
    settings?.enable_telegram !== false;
  const showWa =
    !!manager.whatsapp_link &&
    manager.show_whatsapp !== false &&
    settings?.enable_whatsapp !== false;
  const bothVisible = showTg && showWa;

  // Urgency countdown — "free for the next 17 minutes". Persists across page
  // reloads via sessionStorage so refreshing doesn't reset the clock.
  const TOTAL_MS = 17 * 60 * 1000;
  const DEADLINE_KEY = 'mgr_free_until';

  const computeEndsAt = () => {
    const stored = parseInt(sessionStorage.getItem(DEADLINE_KEY) || '0', 10);
    const now = Date.now();
    if (stored && stored > now && stored - now <= TOTAL_MS) {
      return stored;
    }
    const fresh = now + TOTAL_MS;
    sessionStorage.setItem(DEADLINE_KEY, String(fresh));
    return fresh;
  };

  const [endsAt] = useState(computeEndsAt);
  const [remaining, setRemaining] = useState(() =>
    Math.max(0, Math.floor((endsAt - Date.now()) / 1000))
  );

  useEffect(() => {
    if (remaining <= 0) return undefined;
    const tick = setInterval(() => {
      const left = Math.max(0, Math.floor((endsAt - Date.now()) / 1000));
      setRemaining(left);
      if (left <= 0) clearInterval(tick);
    }, 1000);
    return () => clearInterval(tick);
  }, [endsAt, remaining]);

  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  const stillFree = remaining > 0;

  return (
    <section className="py-12 px-4" data-testid="manager-section">
      <div className="max-w-xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden" data-testid="manager-card">
          <div className="bg-gradient-to-r from-sky-600 to-blue-700 px-6 py-4 text-white text-center">
            <h2 className="text-lg font-bold">Ваш персональный менеджер</h2>
            <p className="text-sky-100 text-sm">Готов ответить на ваши вопросы</p>
          </div>

          <div className="p-6">
            <div className="flex flex-col items-center">
              {manager.photo && (
                <img
                  src={buildAssetSrc(manager.photo)}
                  alt={manager.name}
                  className="w-28 h-28 rounded-full object-cover border-4 border-sky-100 shadow-lg mb-4"
                />
              )}
              <h3 className="text-xl font-bold text-slate-900">{manager.name}</h3>
              {manager.role && <p className="text-slate-500">{manager.role}</p>}

              <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                <Clock className="w-4 h-4" />
                <span>Ответит в течение {settings?.response_time || '5 минут'}</span>
              </div>

              {stillFree && (
                <div
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 border border-red-200 animate-pulse"
                  data-testid="availability-timer"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                  </span>
                  <span className="text-sm text-red-700 font-medium">
                    Менеджер свободен ещё{' '}
                    <span className="font-mono font-bold tabular-nums">
                      {mm}:{ss}
                    </span>
                  </span>
                </div>
              )}
            </div>

            <div
              className={`mt-6 gap-3 ${
                bothVisible ? 'grid grid-cols-2' : 'flex justify-center'
              }`}
            >
              {showTg && (
                <Button
                  className={`bg-sky-500 hover:bg-sky-600 text-white py-6 ${
                    bothVisible ? '' : 'min-w-[220px]'
                  }`}
                  onClick={() => onContact('telegram')}
                  data-testid="telegram-button"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Telegram
                </Button>
              )}
              {showWa && (
                <Button
                  className={`bg-green-500 hover:bg-green-600 text-white py-6 ${
                    bothVisible ? '' : 'min-w-[220px]'
                  }`}
                  onClick={() => onContact('whatsapp')}
                  data-testid="whatsapp-button"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const VacancyCard = ({ vacancy }) => {
  const flag = toFlagEmoji(vacancy.flag || vacancy.country);

  return (
    <div className="bg-slate-50 rounded-xl p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        {flag && <span className="text-3xl">{flag}</span>}
        <div>
          <h3 className="font-bold text-slate-900">{vacancy.title}</h3>
          <p className="text-sm text-slate-500">{vacancy.country}</p>
        </div>
      </div>
      {vacancy.salary_from && (
        <div className="mt-3 text-green-600 font-semibold">{vacancy.salary_from}</div>
      )}
      {vacancy.description && (
        <p className="mt-2 text-sm text-slate-600">{vacancy.description}</p>
      )}
    </div>
  );
};

const VacancyGrid = ({ vacancies }) => (
  <div className="grid md:grid-cols-2 gap-4">
    {vacancies.map((v) => (
      <VacancyCard key={v.id} vacancy={v} />
    ))}
  </div>
);

const FaqItem = ({ item }) => (
  <AccordionItem value={item.id} className="bg-slate-50 rounded-xl px-4">
    <AccordionTrigger className="text-left hover:no-underline">{item.question}</AccordionTrigger>
    <AccordionContent className="text-slate-600">{item.answer}</AccordionContent>
  </AccordionItem>
);

const ResultBanner = ({ quizConfig }) => (
  <section className="relative overflow-hidden pt-8 sm:pt-12 pb-4 px-4 text-white">
    <div className="relative max-w-2xl mx-auto text-center">
      {/* Success icon - smaller on mobile */}
      <div className="relative w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6">
        <div className="absolute inset-0 rounded-full bg-emerald-500/20 animate-ping" style={{ animationDuration: '2s' }} />
        <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center shadow-lg shadow-emerald-500/40">
          <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
        </div>
      </div>

      <div className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-400/30 backdrop-blur px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm mb-3 sm:mb-5">
        <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
        <span className="text-emerald-200">Проверка пройдена</span>
      </div>

      <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-3 tracking-tight text-white">
        {quizConfig.result_title || 'Поздравляем! Вы подходите под условия'}
      </h1>
      <p className="text-sm sm:text-base text-slate-300 max-w-xl mx-auto">
        {quizConfig.result_text || 'Вам доступна консультация с персональным менеджером'}
      </p>
    </div>
  </section>
);

const TrustPoints = ({ quizConfig }) => (
  <section className="py-6 sm:py-8 px-4">
    <div className="max-w-2xl mx-auto">
      <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
        {(quizConfig.result_trust_points || []).map((label, i) => (
          <div 
            key={i} 
            className="flex items-center gap-2 bg-white/5 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/10 hover:border-emerald-400/40 transition-colors"
          >
            <CheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
            <span className="text-xs sm:text-sm text-slate-200">{label}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const AnalyzingScreen = () => {
  const steps = [
    'Записываем вашу анкету',
    'Анализируем ответы',
    'Подбираем менеджера',
  ];
  const [doneCount, setDoneCount] = useState(0);

  useEffect(() => {
    const timers = steps.map((_, i) =>
      setTimeout(() => setDoneCount((c) => Math.max(c, i + 1)), (i + 1) * 700)
    );
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center p-4 bg-slate-950">
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-950 via-teal-900 to-green-950" />
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -right-40 w-[32rem] h-[32rem] bg-green-500/20 rounded-full blur-3xl" />

      <div className="relative max-w-md w-full text-center" data-testid="analyzing-screen">
        {/* Spinner ring */}
        <div className="relative w-24 h-24 mx-auto mb-8">
          <div className="absolute inset-0 rounded-full border-4 border-white/10" />
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-emerald-400 animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-emerald-400" />
          </div>
        </div>

        <h2 className="text-xl md:text-2xl font-bold text-white mb-8 tracking-tight">
          Обрабатываем вашу заявку
        </h2>

        <ul className="space-y-3 text-left">
          {steps.map((label, i) => {
            const done = i < doneCount;
            const current = i === doneCount;
            return (
              <li
                key={i}
                className={`flex items-center gap-3 p-3 rounded-xl border transition-all duration-500 ${
                  done
                    ? 'bg-emerald-500/10 border-emerald-400/30 opacity-100'
                    : current
                    ? 'bg-white/5 border-white/20 opacity-100'
                    : 'bg-white/5 border-white/10 opacity-40'
                }`}
              >
                <span className="relative w-5 h-5 shrink-0">
                  {done ? (
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  ) : current ? (
                    <span className="absolute inset-0 rounded-full border-2 border-transparent border-t-emerald-400 animate-spin" />
                  ) : (
                    <span className="absolute inset-1 rounded-full bg-white/20" />
                  )}
                </span>
                <span
                  className={`text-sm ${
                    done ? 'text-emerald-100' : current ? 'text-white' : 'text-slate-400'
                  }`}
                >
                  {label}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

const MessengerChoice = ({ manager, settings, onPick }) => {
  if (!manager) return null;

  const showTg =
    !!manager.telegram_link &&
    manager.show_telegram !== false &&
    settings?.enable_telegram !== false;
  const showWa =
    !!manager.whatsapp_link &&
    manager.show_whatsapp !== false &&
    settings?.enable_whatsapp !== false;

  if (!showTg && !showWa) return null;
  const bothVisible = showTg && showWa;

  return (
    <section className="py-4 sm:py-6 px-4" data-testid="messenger-choice">
      <div className="max-w-md mx-auto">
        {/* Glass card */}
        <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-5 sm:p-6">
          <div className="text-center mb-4 sm:mb-5">
            <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-emerald-500/20 border border-emerald-400/30 mb-3 sm:mb-4">
              <MessageCircle className="w-6 h-6 sm:w-7 sm:h-7 text-emerald-400" />
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-white mb-1.5 tracking-tight">
              Остался последний шаг
            </h2>
            <p className="text-slate-300 text-sm">
              В каком мессенджере вам удобнее общаться?
            </p>
          </div>

          <div className={`gap-3 ${bothVisible ? 'grid grid-cols-2' : 'flex justify-center'}`}>
            {showWa && (
              <Button
                size="lg"
                className={`bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white py-5 sm:py-6 rounded-xl shadow-lg shadow-green-500/30 transition-all hover:scale-[1.02] hover:shadow-xl hover:shadow-green-400/40 ${
                  bothVisible ? 'w-full' : 'min-w-[240px]'
                }`}
                onClick={() => onPick('whatsapp')}
                data-testid="whatsapp-button"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp
              </Button>
            )}
            {showTg && (
              <Button
                size="lg"
                className={`bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white py-5 sm:py-6 rounded-xl shadow-lg shadow-sky-500/30 transition-all hover:scale-[1.02] hover:shadow-xl hover:shadow-sky-400/40 ${
                  bothVisible ? 'w-full' : 'min-w-[240px]'
                }`}
                onClick={() => onPick('telegram')}
                data-testid="telegram-button"
              >
                <Send className="w-5 h-5 mr-2" />
                Telegram
              </Button>
            )}
          </div>

          <p className="mt-4 text-xs text-slate-400 text-center">
            Мы уже подготовили сообщение — просто нажмите «Отправить»
          </p>
        </div>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function LandingPage() {
  const [bootstrap, setBootstrap] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lightboxItem, setLightboxItem] = useState(null);

  // Quiz state (persisted across reloads via sessionStorage)
  const QUIZ_DONE_KEY = 'quiz_done';
  const CONTACT_KEY = 'quiz_contact';
  const PICKED_KEY = 'quiz_picked';
  const WORK_TYPE_KEY = 'quiz_work_type';
  const VACANCY_KEY = 'quiz_vacancy';
  
  const [quizBegan, setQuizBegan] = useState(
    () => sessionStorage.getItem(QUIZ_DONE_KEY) === '1'
  );
  const [quizDone, setQuizDone] = useState(
    () => sessionStorage.getItem(QUIZ_DONE_KEY) === '1'
  );
  const [contact, setContact] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(CONTACT_KEY) || 'null');
    } catch (_e) {
      return null;
    }
  });
  const [stepIndex, setStepIndex] = useState(0);
  const [picked, setPicked] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(PICKED_KEY) || '[]');
    } catch (_e) {
      return [];
    }
  });
  const [analyzing, setAnalyzing] = useState(false);
  const [showVacancyPause, setShowVacancyPause] = useState(false);
  const [selectedWorkType, setSelectedWorkType] = useState(
    () => sessionStorage.getItem(WORK_TYPE_KEY) || ''
  );
  const [selectedVacancy, setSelectedVacancy] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(VACANCY_KEY) || 'null');
    } catch (_e) {
      return null;
    }
  });

  const siteSettings = bootstrap?.settings;
  const manager = bootstrap?.manager;
  const quizBlock = bootstrap?.quiz;
  const questionList = useMemo(() => quizBlock?.questions || [], [quizBlock]);
  const quizConfig = quizBlock?.settings || {};

  // ---- effects -----------------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data } = await getLandingData();
        if (!cancelled) setBootstrap(data);
      } catch (e) {
        console.error('Failed to load landing data:', e);
      } finally {
        if (!cancelled) setIsLoading(false);
      }

      try {
        const utm = readUtm();
        await trackEvent({
          event_type: 'pageview',
          page_url: window.location.href,
          referrer: document.referrer || '',
          ...utm,
        });
      } catch (e) {
        console.error('Failed to track pageview:', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Pixel fan-out once bootstrap resolves.
  useEffect(() => {
    if (!bootstrap) return;
    const metaIds = bootstrap.pixel_ids?.length
      ? bootstrap.pixel_ids
      : bootstrap.pixel_id
      ? [bootstrap.pixel_id]
      : [];
    injectMetaPixels(metaIds);
    injectTikTokPixels(bootstrap.tiktok_pixel_ids || []);
  }, [bootstrap]);

  // ---- handlers ----------------------------------------------------------

  const handleStartQuiz = () => {
    // Skip contact form if already collected in this session.
    setQuizBegan(true);
    setStepIndex(0);
    setPicked([]);
  };

  const handleContactSubmit = ({ name, phone }) => {
    const c = { name, phone };
    setContact(c);
    sessionStorage.setItem(CONTACT_KEY, JSON.stringify(c));
  };

  // Handle vacancy selection from the showcase
  const handleVacancySelect = (vacancy) => {
    setSelectedVacancy(vacancy);
    if (vacancy) sessionStorage.setItem(VACANCY_KEY, JSON.stringify(vacancy));
    setShowVacancyPause(false);
  };

  const handlePickAnswer = async (option) => {
    const current = questionList[stepIndex];
    const entry = {
      question_id: current.id,
      question_text: current.question,
      answer_id: option.id || '',
      answer_text: option.text || '',
    };
    const updated = [...picked, entry];
    setPicked(updated);
    
    // Save answers immediately to sessionStorage after each answer
    sessionStorage.setItem(PICKED_KEY, JSON.stringify(updated));

    // If this question is the "work type" one, pause and show a vacancy
    // showcase filtered by the user's choice before continuing.
    const isWorkTypeQuestion = /тип работы/i.test(current.question || '');
    const nextIndex = stepIndex + 1;

    if (isWorkTypeQuestion && option.text) {
      setSelectedWorkType(option.text);
      sessionStorage.setItem(WORK_TYPE_KEY, option.text);
      setShowVacancyPause(true);
      setStepIndex(nextIndex);
      return;
    }

    if (nextIndex < questionList.length) {
      setStepIndex(nextIndex);
      return;
    }

    // Quiz finished — show a short "processing" screen (2.5s) while we
    // persist the submission in the background, then reveal the result.
    setAnalyzing(true);
    
    // Save final state to sessionStorage
    if (selectedWorkType) sessionStorage.setItem(WORK_TYPE_KEY, selectedWorkType);
    if (selectedVacancy) sessionStorage.setItem(VACANCY_KEY, JSON.stringify(selectedVacancy));
    
    submitQuiz({
      answers: updated,
      name: contact?.name || '',
      phone: contact?.phone || '',
      work_type: selectedWorkType || '',
      selected_vacancy_title: selectedVacancy?.title || '',
      selected_vacancy_country: selectedVacancy?.country || '',
    }).catch((e) => console.error('Failed to submit quiz:', e));

    setTimeout(() => {
      setAnalyzing(false);
      setQuizDone(true);
      sessionStorage.setItem(QUIZ_DONE_KEY, '1');
      try { window.ttq?.track('CompleteRegistration'); } catch (_e) { /* noop */ }
    }, 2500);
  };

  const handleContact = async (channel) => {
    if (!manager) return;

    let destination = channel === 'telegram' ? manager.telegram_link : manager.whatsapp_link;
    if (!destination) return;

    // Build the auto-message with key quiz data:
    // Name, Country (citizenship), Age, Chosen Vacancy, Passport status
    const lines = ['Здравствуйте! Я прошёл(а) анкету на сайте.', ''];
    
    // Name from contact form
    if (contact?.name) lines.push(`Имя: ${contact.name}`);
    
    // Country (citizenship) - откуда человек
    const countryAnswer = picked.find(a => /гражданств|стран/i.test(a.question_text || ''));
    if (countryAnswer?.answer_text) lines.push(`Гражданство: ${countryAnswer.answer_text}`);
    
    // Age - ищем по вопросу "Сколько вам лет"
    const ageAnswer = picked.find(a => /сколько.*лет|возраст/i.test(a.question_text || ''));
    if (ageAnswer?.answer_text) lines.push(`Возраст: ${ageAnswer.answer_text}`);
    
    // Work type (Склад, Водитель, Ферма и т.д.)
    if (selectedWorkType) {
      lines.push(`Интересующая работа: ${selectedWorkType}`);
    }
    
    // Specific vacancy chosen (Водитель склада Amazon и т.д.)
    if (selectedVacancy?.title) {
      lines.push(`Вакансия: ${selectedVacancy.title}`);
    }
    
    // Desired country of work (Великобритания, Германия и т.д.)
    if (selectedVacancy?.country) {
      lines.push(`Желаемая страна: ${selectedVacancy.country}`);
    }
    
    // Passport status
    const passportAnswer = picked.find(a => /загранпаспорт|паспорт/i.test(a.question_text || ''));
    if (passportAnswer?.answer_text) lines.push(`Загранпаспорт: ${passportAnswer.answer_text}`);
    
    lines.push('', 'Подскажите, пожалуйста, какие дальнейшие действия?');
    const autoMsg = lines.join('\n');

    const encoded = encodeURIComponent(autoMsg);
    destination += destination.includes('?') ? `&text=${encoded}` : `?text=${encoded}`;

    window.open(destination, '_blank');

    // Track the click both server-side (CAPI) and client-side (pixels).
    try {
      const params = new URLSearchParams(window.location.search);
      const fbclid = params.get('fbclid') || '';
      const fbc = readCookie('_fbc') || (fbclid ? `fb.1.${Date.now()}.${fbclid}` : '');

      trackEvent({
        event_type: channel === 'telegram' ? 'click_telegram' : 'click_whatsapp',
        manager_id: manager.id,
        page_url: window.location.href,
        referrer: document.referrer || '',
        ...readUtm(),
        fbp: readCookie('_fbp') || '',
        fbc,
      });

      window.fbq?.('track', 'Lead', { content_name: channel, manager_id: manager.id });
      window.ttq?.track('ClickButton', { content_name: channel });
    } catch (e) {
      console.error('Failed to track click:', e);
    }
  };

  // ---- render ------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-sky-500 border-t-transparent" />
      </div>
    );
  }

  const progressPct = questionList.length
    ? ((stepIndex + (quizDone ? 1 : 0)) / questionList.length) * 100
    : 0;
  const quizActive = quizConfig?.is_enabled && questionList.length > 0;

  // 1) Hero / intro
  if (quizActive && !quizBegan) {
    return (
      <QuizIntro
        config={quizConfig}
        questionCount={questionList.length}
        onStart={handleStartQuiz}
      />
    );
  }

  // 2) Contact form (only if we haven't collected name + phone yet)
  if (quizActive && quizBegan && !contact && !quizDone) {
    return <ContactForm onSubmit={handleContactSubmit} />;
  }

  // 3) Vacancy showcase interstitial (после "тип работы")
  if (quizActive && showVacancyPause && !quizDone) {
    return (
      <VacancyShowcase
        workType={selectedWorkType}
        vacancies={bootstrap?.vacancies || []}
        onSelect={handleVacancySelect}
      />
    );
  }

  // 4) In-quiz questions
  if (quizActive && quizBegan && contact && !quizDone && !analyzing) {
    return (
      <QuizQuestionView
        question={questionList[stepIndex]}
        stepIndex={stepIndex}
        total={questionList.length}
        progressPct={progressPct}
        onPick={handlePickAnswer}
      />
    );
  }

  // 5) Analyzing / processing screen (~2.5s)
  if (analyzing) {
    return <AnalyzingScreen />;
  }

  // 6) Final: congratulations + choose messenger
  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-950" data-testid="final-screen">
      {/* Unified dark gradient background - same as quiz steps */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-950 via-teal-900 to-green-950" />
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -right-40 w-[32rem] h-[32rem] bg-green-500/20 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] bg-teal-500/10 rounded-full blur-3xl" />

      {/* Content - reordered: Banner -> Messenger -> TrustPoints */}
      <div className="relative z-10 flex flex-col min-h-screen">
        {quizActive && quizDone && <ResultBanner quizConfig={quizConfig} />}
        <MessengerChoice manager={manager} settings={siteSettings} onPick={handleContact} />
        {quizActive && quizDone && <TrustPoints quizConfig={quizConfig} />}
      </div>

      {lightboxItem && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxItem(null)}
        >
          <button className="absolute top-4 right-4 text-white">
            <X className="w-8 h-8" />
          </button>
          <img
            src={buildAssetSrc(lightboxItem.url)}
            alt={lightboxItem.title || ''}
            className="max-w-full max-h-[90vh] object-contain"
          />
        </div>
      )}
    </div>
  );
}


// Silence unused-import warnings for icons kept for future use.
void Play;
